import { supabase } from "../../config/supabase";

export interface Plan {
  id: string;
  company_id: string;
  sector_id: string;
  crop_id: string;
  plan_id: string;
  plan_name: string;
  plan_date: string;
  created_at?: string;
  updated_at?: string;
  meta: any;
}

export class PlanService {
  async createPlan(plan: Plan) {
    const { data: planData, error: planError } = await supabase
      .from("plans")
      .insert({
        user_id: (await supabase.auth.getUser()).data.user?.id,
        company_id: plan.company_id,
        crop_id: plan.crop_id,
        sector_id: plan.sector_id,
        plan_name: plan.plan_name,
        plan_date: plan.plan_date,
        meta: plan.meta,
      })
      .select()
      .single();

    if (planError) throw planError;

    return planData;
  }

  /**
   * Get a plan by its ID including all related data
   */
  async getPlanById(planId: string) {
    const { data: plan, error: planError } = await supabase
      .from("plans")
      .select(
        `
        *,
        companies (company_name),
        sectors (sector_name),
        crops (crop_name)
      `
      )
      .eq("id", planId)
      .single();

    if (planError) throw planError;

    return {
      ...plan,
    };
  }

  /**
   * Get all plans for a user
   */
  async listPlans() {
    // Get current user for RLS policy
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
      .from("plans")
      .select(
        `
        *,
        companies (company_name),
        sectors (sector_name),
        crops (crop_name)
      `
      )
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return {
      success: true,
      data: data.map((plan) => ({
        planId: plan.id,
        ...plan.meta,
      })),
    };
  }

  /**
   * Update a plan and its associated data
   */
  async updatePlan(planId: string, plan: Partial<Plan>) {
    const { data: updatedPlan, error: planError } = await supabase
      .from("plans")
      .update(plan)
      .eq("id", planId)
      .select()
      .single();

    if (planError) throw planError;

    return updatedPlan;
  }

  /**
   * Delete a plan and all associated data
   */
  async deletePlan(planId: string) {
    // Finally delete the plan
    const { error: planError } = await supabase
      .from("plans")
      .delete()
      .eq("id", planId);

    if (planError) throw planError;

    return true;
  }
}

export const planService = new PlanService();
